// Contrôleur Stimulus pour le scanner de code-barres
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { 
    enabled: { type: Boolean, default: true }
  }

  connect() {
    console.log("Barcode scanner controller connected")
    this.barcodeBuffer = ""
    this.lastKeyTime = 0
    this.testMode = false
    
    // S'assurer qu'un seul contrôleur de scanner est actif
    if (window.activeBarcodeScanner) {
      window.activeBarcodeScanner.disconnect()
    }
    window.activeBarcodeScanner = this
    
    document.addEventListener('keydown', this.handleKeyDown.bind(this))
  }

  disconnect() {
    console.log("Barcode scanner controller disconnected")
    document.removeEventListener('keydown', this.handleKeyDown.bind(this))
    if (window.activeBarcodeScanner === this) {
      window.activeBarcodeScanner = null
    }
  }

  handleKeyDown(event) {
    // Ne traiter que si le scanner est activé
    if (!this.enabledValue) {
      return
    }

    // Ignorer les touches si on est dans un champ de saisie
    const activeElement = document.activeElement
    if (activeElement && (
      activeElement.tagName === 'INPUT' || 
      activeElement.tagName === 'TEXTAREA' || 
      activeElement.contentEditable === 'true'
    )) {
      return
    }

    const currentTime = Date.now()
    
    // Si plus de 100ms entre les touches, c'est probablement de la saisie manuelle
    // (Sauf en mode test où on ignore cette vérification)
    if (!this.testMode && currentTime - this.lastKeyTime > 100) {
      this.barcodeBuffer = ""
    }
    
    this.lastKeyTime = currentTime

    // Capturer uniquement les caractères alphanumériques et certains symboles
    if (event.key.match(/^[a-zA-Z0-9\-_]$/)) {
      this.barcodeBuffer += event.key
    }
    
    // Le code-barres se termine généralement par Entrée
    if (event.key === 'Enter' && this.barcodeBuffer.length > 5) {
      event.preventDefault()
      this.processBarcodeInput(this.barcodeBuffer)
      this.barcodeBuffer = ""
    }
  }

  async processBarcodeInput(barcode) {
    try {
      // Envoyer le code-barres au serveur - utilise le ticket actif automatiquement
      const formData = new FormData()
      formData.append('barcode', barcode)
      
      const response = await fetch(`/pos/ticket_items`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'text/vnd.turbo-stream.html'
        },
        body: formData
      })
      
      if (response.ok) {
        // Laisser Turbo Stream s'occuper de la mise à jour de l'interface
        const turboStream = await response.text()
        Turbo.renderStreamMessage(turboStream)
      } else {
        this.showMessage(`Erreur lors du scan du code-barres: ${barcode}`, 'error')
      }
    } catch (error) {
      console.error('Erreur de réseau lors du scan:', error)
      this.showMessage('Erreur de connexion lors du scan', 'error')
    }
  }

  showMessage(text, type) {
    const notification = document.createElement('div')
    notification.className = `fixed top-4 right-4 px-4 py-2 rounded-md text-white text-sm font-medium z-50 ${
      type === 'success' ? 'bg-green-500' : 'bg-red-500'
    }`
    notification.textContent = text
    
    document.body.appendChild(notification)
    
    setTimeout(() => {
      notification.remove()
    }, 3000)
  }

  // Méthodes publiques pour contrôler le scanner
  enable() {
    this.enabledValue = true
    console.log("Barcode scanner enabled")
  }

  disable() {
    this.enabledValue = false
    console.log("Barcode scanner disabled")
  }

  toggle() {
    this.enabledValue = !this.enabledValue
    console.log(`Barcode scanner ${this.enabledValue ? 'enabled' : 'disabled'}`)
  }

  // Méthode de test pour simuler un scan
  testScan(event = null) {
    // Si c'est appelé via un événement (clic sur bouton), utiliser un code par défaut
    const barcode = (event && typeof event === 'object' && event.type) 
      ? '1234567890124'  // Code de la crème solaire SPF 50
      : (event || '1234567890124')
    
    console.log(`Testing barcode scan: ${barcode}`)
    this.processBarcodeInput(barcode)
  }

  // Mode de test qui ignore la détection de vitesse
  enableTestMode() {
    this.testMode = true
    console.log("Test mode enabled - speed detection disabled")
  }

  disableTestMode() {
    this.testMode = false
    console.log("Test mode disabled - speed detection enabled")
  }
}
